import React, { useState, useEffect } from 'react';
import Textarea from '../../../components/ui/Textarea';
import EmptyState from '../../../components/ui/EmptyState';
import { useToast } from '../../../components/ui/Toast';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { groupService } from '../../../services/groupService';
import { announcementService } from '../../../services/announcementService';
import { formatDateTime } from '../../../utils/formatters';
import GroupEditor from './GroupEditor';

/**
 * Conference Management → Announcements tab.
 * Chairs/organizers publish in-app announcements (one-off or pinned FAQ) to a
 * union/intersection of participant groups, and manage those groups.
 */
const AnnouncementsTab = ({ conferenceId }) => {
  // Replaces alert(): non-blocking, styled, translatable, and errors
  // persist until dismissed instead of vanishing.
  const { success: toastSuccess, error: toastError } = useToast();
  const { t } = useTranslation('notifications');
  const k = (key, opts) => t(`announcements.${key}`, opts);
  const c = (key, opts) => t(`announcements.compose.${key}`, opts);
  const g = (key, opts) => t(`announcements.groups.${key}`, opts);

  // mode: 'list' | 'compose' | 'groups' | 'groupEditor'
  const [mode, setMode] = useState('list');
  const [groups, setGroups] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);

  // compose form
  const [form, setForm] = useState({ title: '', body: '', groupIds: new Set(), audienceOp: 'union', isPinned: false });
  const [recipientCount, setRecipientCount] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { loadAll(); }, [conferenceId]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [gr, an] = await Promise.all([
        groupService.getGroups(conferenceId),
        announcementService.getAnnouncements(conferenceId)
      ]);
      setGroups(gr || []);
      setAnnouncements(an || []);
    } finally {
      setLoading(false);
    }
  };

  // Resolve recipient count when composing a new announcement
  useEffect(() => {
    if (mode !== 'compose' || editingAnnouncement) return;
    const ids = Array.from(form?.groupIds || []);
    if (!ids?.length) { setRecipientCount(0); return; }
    let cancelled = false;
    (async () => {
      setResolving(true);
      try {
        const users = await announcementService.resolveAudience(conferenceId, ids, form?.audienceOp);
        if (!cancelled) setRecipientCount(users?.length || 0);
      } catch { if (!cancelled) setRecipientCount(null); }
      finally { if (!cancelled) setResolving(false); }
    })();
    return () => { cancelled = true; };
  }, [form?.groupIds, form?.audienceOp, mode, editingAnnouncement, conferenceId]);

  const startNewAnnouncement = () => {
    setEditingAnnouncement(null);
    setForm({ title: '', body: '', groupIds: new Set(), audienceOp: 'union', isPinned: false });
    setRecipientCount(null);
    setError(null);
    setMode('compose');
  };

  const startEditAnnouncement = (a) => {
    setEditingAnnouncement(a);
    setForm({ title: a?.title, body: a?.body, groupIds: new Set(a?.groupIds || []), audienceOp: a?.audienceOp, isPinned: a?.isPinned });
    setError(null);
    setMode('compose');
  };

  const toggleGroupInForm = (id) => {
    setForm(prev => {
      const next = new Set(prev?.groupIds);
      next.has(id) ? next.delete(id) : next.add(id);
      return { ...prev, groupIds: next };
    });
  };

  const handlePublish = async () => {
    if (!form?.title?.trim() || !form?.body?.trim()) { setError(c('requiredFields')); return; }
    setPublishing(true);
    setError(null);
    try {
      if (editingAnnouncement) {
        await announcementService.updateAnnouncement(editingAnnouncement?.id, {
          title: form?.title, body: form?.body, isPinned: form?.isPinned
        });
      } else {
        if (!form?.groupIds?.size) { setError(c('selectGroup')); setPublishing(false); return; }
        await announcementService.publish({
          conferenceId,
          title: form?.title,
          body: form?.body,
          groupIds: Array.from(form?.groupIds),
          audienceOp: form?.audienceOp,
          isPinned: form?.isPinned
        });
      }
      await loadAll();
      setMode('list');
    } catch (e) {
      setError(e?.message || c('publishFailed'));
    } finally {
      setPublishing(false);
    }
  };

  const handleDeleteAnnouncement = async (a) => {
    if (!window.confirm(k('confirmDelete'))) return;
    try {
      await announcementService.deleteAnnouncement(a?.id, a?.notificationId);
      await loadAll();
    } catch (e) { toastError(e?.message || k('deleteFailed')); }
  };

  const handleTogglePin = async (a) => {
    try {
      await announcementService.setPinned(a?.id, !a?.isPinned);
      await loadAll();
    } catch (e) { toastError(e?.message || k('updateFailed')); }
  };

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(g('confirmDelete', { name: group?.name }))) return;
    try {
      await groupService.deleteGroup(group?.id);
      await loadAll();
    } catch (e) { toastError(e?.message || g('deleteFailed')); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Icon name="Loader2" size={36} className="text-primary animate-spin" /></div>;
  }

  // ── Group editor ──
  if (mode === 'groupEditor') {
    return (
      <GroupEditor
        conferenceId={conferenceId}
        group={editingGroup}
        onClose={() => { setMode('groups'); loadAll(); }}
        onChanged={loadAll}
      />
    );
  }

  // ── Groups management ──
  if (mode === 'groups') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <button onClick={() => setMode('list')} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
            <Icon name="ChevronLeft" size={14} /> {g('backToAnnouncements')}
          </button>
          <Button size="sm" onClick={() => { setEditingGroup(null); setMode('groupEditor'); }} iconName="Plus" iconPosition="left">
            {g('newGroup')}
          </Button>
        </div>
        <h3 className="text-lg font-semibold text-foreground">{g('title')}</h3>
        {groups?.length === 0 ? (
          <p className="text-sm text-muted-foreground">{g('empty')}</p>
        ) : (
          <div className="space-y-2">
            {groups?.map(group => (
              <div key={group?.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-card">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">{group?.name}</p>
                  {group?.description && <p className="text-xs text-muted-foreground mt-0.5 truncate">{group?.description}</p>}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {group?.roleSources?.map(s => (
                      <span key={s} className="px-1.5 py-0.5 text-[10px] font-medium bg-primary/10 text-primary">
                        {g(`roleSources.${s}`)}
                      </span>
                    ))}
                    {typeof group?.memberCount === 'number' && group?.memberCount > 0 && (
                      <span className="px-1.5 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground">
                        {g('individualCount', { count: group?.memberCount })}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => { setEditingGroup(group); setMode('groupEditor'); }} iconName="Pencil" iconPosition="left">
                    {g('edit')}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDeleteGroup(group)} iconName="Trash2" className="text-muted-foreground hover:text-error" title={g('deleteTitle')} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Compose ──
  if (mode === 'compose') {
    return (
      <div className="space-y-5 max-w-3xl">
        <button onClick={() => setMode('list')} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
          <Icon name="ChevronLeft" size={14} /> {c('backToList')}
        </button>
        <h3 className="text-lg font-semibold text-foreground">{editingAnnouncement ? c('editTitle') : c('newTitle')}</h3>
        {error && <p className="text-sm text-error">{error}</p>}

        <Input label={c('title')} value={form?.title} onChange={(e) => setForm(prev => ({ ...prev, title: e?.target?.value }))} placeholder={c('titlePlaceholder')} />

        <div>
          <Textarea
            label={c('message')}
            rows={10}
            value={form?.body || ''}
            onChange={(e) => setForm(prev => ({ ...prev, body: e?.target?.value }))}
            placeholder={c('messagePlaceholder')}
          />
        </div>

        {editingAnnouncement ? (
          <p className="text-xs text-muted-foreground">{c('audienceLocked')}</p>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">{c('audience')}</label>
              {groups?.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  {c('noGroups')}{' '}
                  <button className="text-primary hover:underline" onClick={() => setMode('groups')}>{c('createFirst')}</button>.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {groups?.map(group => {
                    const active = form?.groupIds?.has(group?.id);
                    return (
                      <button
                        key={group?.id}
                        type="button"
                        onClick={() => toggleGroupInForm(group?.id)}
                        className={`px-3 py-1.5 text-xs font-medium border transition-colors ${
                          active ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground border-border hover:text-foreground'
                        }`}
                      >
                        {active && <Icon name="Check" size={12} className="inline mr-1" />}{group?.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {form?.groupIds?.size > 1 && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{c('combineBy')}</label>
                <div className="flex gap-2">
                  {[{ v: 'union', l: c('union') }, { v: 'intersection', l: c('intersection') }]?.map(o => (
                    <button
                      key={o?.v}
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, audienceOp: o?.v }))}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        form?.audienceOp === o?.v ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground border-border hover:text-foreground'
                      }`}
                    >
                      {o?.l}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="p-3 bg-muted/60 rounded-lg flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">{c('recipients')}</span>
              {resolving ? <Icon name="Loader2" size={16} className="animate-spin text-muted-foreground" />
                : <span className="text-lg font-bold text-primary">{recipientCount ?? 0}</span>}
            </div>
          </>
        )}

        <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
          <input type="checkbox" checked={form?.isPinned} onChange={(e) => setForm(prev => ({ ...prev, isPinned: e?.target?.checked }))} className="w-4 h-4" />
          {c('pinAsFaq')}
        </label>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
          <Button variant="outline" onClick={() => setMode('list')} disabled={publishing}>{c('cancel')}</Button>
          <Button onClick={handlePublish} disabled={publishing} iconName="Send" iconPosition="left">
            {publishing ? c('publishing') : editingAnnouncement ? c('saveChanges') : c('publish')}
          </Button>
        </div>
      </div>
    );
  }

  // ── List ──
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-foreground">{k('tabTitle')}</h2>
          <p className="text-xs text-muted-foreground mt-1">{k('tabSubtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setMode('groups')} iconName="Users" iconPosition="left">
            {k('manageGroups')}
          </Button>
          <Button size="sm" onClick={startNewAnnouncement} iconName="Plus" iconPosition="left">
            {k('newAnnouncement')}
          </Button>
        </div>
      </div>

      {announcements?.length === 0 ? (
        <div className="bg-card border border-border rounded-lg">
          <EmptyState
            compact
            icon="Megaphone"
            title={k('empty')}
            body={k('emptyBody', { defaultValue: 'Announcements you publish are shown to everyone on the conference page.' })}
          />
        </div>
      ) : (
        <div className="space-y-2">
          {announcements?.map(a => (
            <div key={a?.id} className="p-4 rounded-lg border border-border bg-card">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {a?.isPinned && (
                      <span className="px-1.5 py-0.5 text-[10px] font-medium bg-warning-subtle text-warning">
                        <Icon name="Pin" size={10} className="inline mr-0.5" />{k('pinned')}
                      </span>
                    )}
                    <p className="text-sm font-semibold text-foreground">{a?.title}</p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap line-clamp-3">{a?.body}</p>
                  <p className="text-[11px] text-muted-foreground mt-2">
                    {formatDateTime(a?.publishedAt)}
                    {typeof a?.recipientCount === 'number' ? ` · ${k('recipientCount', { count: a?.recipientCount })}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => handleTogglePin(a)} iconName={a?.isPinned ? 'PinOff' : 'Pin'} title={a?.isPinned ? k('unpin') : k('pin')} />
                  <Button variant="ghost" size="icon" onClick={() => startEditAnnouncement(a)} iconName="Pencil" title={k('edit')} />
                  <Button variant="ghost" size="icon" onClick={() => handleDeleteAnnouncement(a)} iconName="Trash2" className="text-muted-foreground hover:text-error" title={k('delete')} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AnnouncementsTab;
